import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {useMutation, useQuery} from "@apollo/client";
import {ADD_TOUR_LABEL, GET_TOUR_ITEMS_LIST, GET_TOURS_LABEL_LIST} from "../gql/tours-gql";
import {LoginButton} from "../../styles/Styles";
import {Languages} from "../common/languages";
import {toast} from "react-toastify";
import styled from "styled-components";
import {FaCheck} from "react-icons/fa";
import Loader from "../common/Loader";

const FormContainer = styled.div`
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #4CAF50;
    }
`;

const Select = styled.select`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    background-color: white;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #4CAF50;
    }
`;

const ErrorMessage = styled.span`
    color: #f44336;
    font-size: 0.9rem;
    margin-top: 0.5rem;
    display: block;
`;

const FormTitle = styled.h2`
    margin-bottom: 2rem;
    color: #333;
    text-align: center;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;

const LanguagesList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.5rem;
    margin-top: 0.5rem;
`;

const LanguageItem = styled.div<{ selected: boolean }>`
    padding: 0.8rem;
    border: 1px solid ${props => props.selected ? '#4CAF50' : '#ddd'};
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${props => props.selected ? '#e8f5e9' : 'white'};

    &:hover {
        border-color: #4CAF50;
    }
`;

const AddTourForm: React.FC = () => {
    const [langs, setLang] = useState<string[]>([Languages.ENGLISH]);
    const {data: toursItems, loading: toursLoading} = useQuery(GET_TOUR_ITEMS_LIST);
    const labels = toursItems?.getTourItemsList || [];

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const LanguagesArray = Object.values(Languages);

    const toggleLang = (lang: string) => {
        const exists = langs.includes(lang);
        if (!exists) {
            setLang((prev) => [lang, ...prev]);
        } else {
            const arrayWithoutLang = langs.filter((el) => el !== lang);
            setLang(arrayWithoutLang);
        }
    }

    const [addTourMutation] = useMutation(ADD_TOUR_LABEL, {
        refetchQueries: [{ query: GET_TOURS_LABEL_LIST }],
        onCompleted: (data) => {
            toast.success("Added successfully!");
            reset();
            setLang([Languages.ENGLISH]);
        }
    });

    const onSubmit = async(data: any) => {
        const selectedLabel = labels.find((label: any) => label._id === data.tourItem);
        await addTourMutation({
            variables: {
                tourLabelInput: {
                    title: {_id: selectedLabel._id, title: ""},
                    maxClients: parseFloat(data.maxClients),
                    price: parseFloat(data.price),
                    ticketRate: {
                        adultRate: parseFloat(data.adultRate),
                        youthRate: parseFloat(data.youthRate),
                        childRate: parseFloat(data.childRate),
                        babyRate: parseFloat(data.babyRate),
                    },
                    language: [...langs],
                    availability: true,
                    reservationCode: data.reservationCode,
                }
            }
        })
    }

    if (toursLoading) return <Loader />;

    return (
        <FormContainer>
            <FormTitle>Add New Tour</FormTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <Label htmlFor="tourItem">Tour Label</Label>
                    <Select {...register("tourItem", { required: true })}>
                        <option value="">Select a tour label</option>
                        {labels.map((label: any) => (
                            <option key={label._id} value={label._id}>
                                {label.title}
                            </option>
                        ))}
                    </Select>
                    {errors.tourItem && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="maxClients">Max Clients</Label>
                    <Input
                        {...register("maxClients", { required: true })}
                        type="number"
                        placeholder="Enter maximum number of clients"
                    />
                    {errors.maxClients && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="price">Base Price</Label>
                    <Input
                        {...register("price", {
                            required: true,
                            pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please use decimal point (.) and up to 2 decimal places"
                            }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter base price (e.g. 23.10)"
                        onKeyUp={(e) => {
                            if (e.key === ',') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.price && <ErrorMessage>{"This field is required"}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="adultRate">Adult Tickets Rate</Label>
                    <Input
                        {...register("adultRate", {
                            required: true,
                            pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please use decimal point (.) and up to 2 decimal places"
                            }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter adult ticket rate (e.g. 23.10)"
                        onKeyUp={(e) => {
                            if (e.key === ',') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.adultRate && <ErrorMessage>{"This field is required"}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="youthRate">Youth Tickets Rate</Label>
                    <Input
                        {...register("youthRate", {
                            required: true,
                            pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please use decimal point (.) and up to 2 decimal places"
                            }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter youth ticket rate (e.g. 23.10)"
                        onKeyUp={(e) => {
                            if (e.key === ',') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.youthRate && <ErrorMessage>{"This field is required"}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="childRate">Child Tickets Rate</Label>
                    <Input
                        {...register("childRate", {
                            required: true,
                            pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please use decimal point (.) and up to 2 decimal places"
                            }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter child ticket rate (e.g. 23.10)"
                        onKeyUp={(e) => {
                            if (e.key === ',') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.childRate && <ErrorMessage>{ "This field is required"}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="babyRate">Enfant Tickets Rate</Label>
                    <Input
                        {...register("babyRate", {
                            required: true,
                            pattern: {
                                value: /^\d*\.?\d{0,2}$/,
                                message: "Please use decimal point (.) and up to 2 decimal places"
                            }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter enfant ticket rate (e.g. 23.10)"
                        onKeyUp={(e) => {
                            if (e.key === ',') {
                                e.preventDefault();
                            }
                        }}
                    />
                    {errors.babyRate && <ErrorMessage>{"This field is required"}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="reservationCode">Reservation Code</Label>
                    <Input
                        {...register("reservationCode", { required: true })}
                        type="text"
                        placeholder="Enter reservationCode"
                    />
                    {errors.reservationCode && <ErrorMessage>This field is required</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                    <Label>Select Languages</Label>
                    <LanguagesList>
                        {LanguagesArray.map((lang) => (
                            <LanguageItem
                                key={lang}
                                selected={langs.includes(lang)}
                                onClick={() => toggleLang(lang)}
                            >
                                {lang}
                                {langs.includes(lang) && <FaCheck color="#4CAF50" />}
                            </LanguageItem>
                        ))}
                    </LanguagesList>
                </FormGroup>

                <ButtonContainer>
                    <LoginButton type="submit">Add Tour</LoginButton>
                </ButtonContainer>
            </form>
        </FormContainer>
    );
}

export default AddTourForm;
